<template>
  <div class="space-y-6">
    <notifications position="top center" />
    <div class="bg-white shadow-sm border rounded-lg">
      <div class="px-6 py-4 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">Salesforce Campaign Health</h1>
          <p class="text-sm text-gray-600 mt-1">High-level view across recent submitted campaigns.</p>
        </div>
        <div class="flex items-center gap-3">
          <button @click="loadRows" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center">
            <svg class="w-4 h-4 mr-2" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>
      <div class="px-6 pb-4">
        <form @submit.prevent="loadRows" class="flex flex-wrap items-center gap-3 text-sm">
          <label class="inline-flex items-center">Source:
            <select v-model="filters.source" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="logs">Sync Logs</option>
              <option value="recent">Recent Campaigns</option>
            </select>
          </label>
          <label class="inline-flex items-center">Days:
            <input type="number" v-model.number="filters.days" min="1" class="ml-2 w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </label>
          <label class="inline-flex items-center">Limit:
            <input type="number" v-model.number="filters.limit" min="1" class="ml-2 w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </label>
          <label class="inline-flex items-center">Opp name contains:
            <input type="text" v-model="filters.dp_name" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </label>
          <label class="inline-flex items-center">Chunk size:
            <select v-model.number="chunkSize" class="ml-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
            </select>
          </label>
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="showProblemsOnly" class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            Problems only
          </label>
          <label class="inline-flex items-center">
            <input type="checkbox" v-model="bypassCache" class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            Force refresh
          </label>
          <button type="submit" class="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Apply</button>
          <button type="button" @click="resyncAllProblems" :disabled="bulkResyncing || problemRowsCount === 0" class="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Resync All Problems ({{ problemRowsCount }})</button>
          <button type="button" @click="toggleIgnoredSection" class="px-3 py-2 bg-amber-50 border border-amber-300 text-amber-700 rounded-md hover:bg-amber-100">
            {{ showIgnoredSection ? 'Hide' : 'Show' }} Ignored ({{ ignoredCampaigns.length }})
          </button>
          <button type="button" @click="toggleNestedSection" class="px-3 py-2 bg-red-50 border border-red-300 text-red-700 rounded-md hover:bg-red-100">
            Check Nested Campaigns
          </button>
        </form>
      </div>
    </div>

    <!-- Ignored Campaigns Section -->
    <div v-if="showIgnoredSection" class="bg-amber-50 rounded-lg shadow border border-amber-200 overflow-hidden mb-4">
      <div class="px-6 py-4 border-b border-amber-200">
        <h3 class="text-lg font-medium text-amber-900">Ignored Campaigns</h3>
        <p class="text-sm text-amber-700 mt-1">These campaigns are temporarily hidden from the health report.</p>
      </div>
      <div v-if="loadingIgnored" class="p-4">
        <div class="text-sm text-amber-700">Loading ignored campaigns...</div>
      </div>
      <div v-else-if="ignoredCampaigns.length === 0" class="p-4">
        <div class="text-sm text-amber-700">No campaigns are currently ignored.</div>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-amber-200">
          <thead class="bg-amber-100">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Campaign</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Ignored By</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Reason</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">Expires</th>
              <th class="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-amber-200">
            <tr v-for="ignored in ignoredCampaigns" :key="ignored.id">
              <td class="px-4 py-2 text-sm">
                <div class="font-medium text-gray-900">{{ ignored.campaign.name }}</div>
                <div class="text-gray-500 text-xs">#{{ ignored.campaign.id }}</div>
              </td>
              <td class="px-4 py-2 text-sm text-gray-700">{{ ignored.ignored_by || 'System' }}</td>
              <td class="px-4 py-2 text-sm text-gray-600">{{ ignored.reason || '—' }}</td>
              <td class="px-4 py-2 text-sm">
                <div class="text-gray-700">{{ ignored.expires_at }}</div>
                <div class="text-xs text-amber-600">{{ ignored.days_remaining }} days left</div>
              </td>
              <td class="px-4 py-2 text-sm text-right">
                <button @click="unignoreCampaign(ignored.campaign.id)" class="px-3 py-1.5 border border-amber-300 rounded-md text-sm bg-white hover:bg-amber-50">
                  Unignore
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Nested Campaigns Section -->
    <div v-if="showNestedSection" class="bg-red-50 rounded-lg shadow border border-red-200 overflow-hidden mb-4">
      <div class="px-6 py-4 border-b border-red-200">
        <h3 class="text-lg font-medium text-red-900">Nested Campaigns in Salesforce</h3>
        <p class="text-sm text-red-700 mt-1">These nested campaigns were synced to Salesforce before the restriction was added.</p>
      </div>
      <div v-if="loadingNested" class="p-4">
        <div class="animate-pulse">Loading nested campaigns...</div>
      </div>
      <div v-else-if="nestedCampaigns.length === 0" class="p-4 text-center text-gray-600">
        <p>No nested campaigns found in Salesforce.</p>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-red-200">
          <thead class="bg-red-100">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Campaign ID</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Campaign Name</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Salesforce Link</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-red-200">
            <tr v-for="nested in nestedCampaigns" :key="nested.campaign_id">
              <td class="px-4 py-2 text-sm text-gray-900">{{ nested.campaign_id }}</td>
              <td class="px-4 py-2 text-sm text-gray-900">{{ nested.campaign_name }}</td>
              <td class="px-4 py-2 text-sm">
                <a v-if="nested.salesforce_link" 
                   :href="nested.salesforce_link" 
                   target="_blank" 
                   class="text-blue-600 hover:text-blue-800 underline">
                  View in Salesforce
                </a>
                <span v-else class="text-gray-500">Not found</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow border overflow-hidden">
      <!-- Top progress bar -->
      <div class="w-full bg-gray-100">
        <div class="h-1 bg-blue-600 transition-all duration-300" :style="{ width: progress + '%' }"></div>
        <div v-if="progress > 0" class="px-4 py-2 text-xs text-gray-600">Loading {{ Math.min(progress, 100) }}% ({{ Math.min(rows.length, campaigns.length) }} / {{ campaigns.length || 0 }})</div>
      </div>

      <div v-if="loading" class="p-4">
        <!-- Skeleton header -->
        <div class="h-5 bg-gray-100 rounded w-32 mb-4"></div>
        <!-- Skeleton rows -->
        <div class="space-y-2">
          <div v-for="i in 6" :key="i" class="grid grid-cols-7 gap-2 items-center">
            <div class="h-4 bg-gray-100 rounded col-span-2"></div>
            <div class="h-4 bg-gray-100 rounded col-span-2"></div>
            <div class="h-4 bg-gray-100 rounded col-span-1"></div>
            <div class="h-4 bg-gray-100 rounded col-span-1"></div>
            <div class="h-4 bg-gray-100 rounded col-span-1"></div>
          </div>
        </div>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unconverted Leads</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DP Opp Count</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accounts</th>
              <th class="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="row in visibleRows" :key="row.campaign.id">
            <tr :class="rowClass(row)">
              <td class="px-4 py-2 text-sm">
                <div class="font-medium text-gray-900">{{ row.campaign.name }}</div>
                <div class="text-gray-500 text-xs">#{{ row.campaign.id }} • {{ row.campaign.created_at }}</div>
              </td>
              <td class="px-4 py-2 text-sm text-gray-700">
                <button v-if="row.email"
                        @click="copyRowEmail(row)"
                        class="inline-flex items-center text-blue-600 hover:text-blue-800"
                        type="button"
                        title="Copy email">
                  <code class="px-1 py-0.5 bg-blue-50 rounded">{{ row.email }}</code>
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-4 py-2 text-sm">
                <span :class="badge(row.metrics.unconverted_leads === 0)">{{ row.metrics.unconverted_leads }}</span>
              </td>
              <td class="px-4 py-2 text-sm">
                <span :class="badge(row.metrics.dig_pink_opps_count === 1)">{{ row.metrics.dig_pink_opps_count }}</span>
              </td>
              <td class="px-4 py-2 text-sm">
                <span :class="badge(row.metrics.accounts_count <= 1)">{{ row.metrics.accounts_count }}</span>
              </td>
              <td class="px-4 py-2 text-sm text-right">
                <a v-if="row.links && row.links.salesforce" :href="row.links.salesforce" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline mr-3">Salesforce</a>
                <a v-if="row.links.detail" :href="row.links.detail" class="text-blue-600 hover:text-blue-800 hover:underline mr-3">Details</a>
                <span v-else class="text-gray-400 text-xs mr-3" :title="row.note">N/A</span>
                <button @click="toggleLogPanel(row)" class="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 mr-2">{{ isLogOpen(row) ? 'Hide Output' : 'View Output' }}</button>
                <button @click="refreshRow(row)" :disabled="refreshing[row.campaign.id]" class="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50 mr-2">
                  <svg v-if="refreshing[row.campaign.id]" class="w-4 h-4 inline mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Refresh
                </button>
                <button @click="resync(row)" :disabled="resyncing[row.campaign.id]" class="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50 mr-2">
                  <svg v-if="resyncing[row.campaign.id]" class="w-4 h-4 inline mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Resync
                </button>
                <button @click="ignoreCampaign(row)" :disabled="ignoring[row.campaign.id]" class="px-3 py-1.5 border border-amber-500 text-amber-700 rounded-md text-sm bg-amber-50 hover:bg-amber-100 disabled:opacity-50" title="Ignore this campaign for 30 days">
                  <svg v-if="ignoring[row.campaign.id]" class="w-4 h-4 inline mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Ignore 30d
                </button>
              </td>
            </tr>
            <tr v-if="isLogOpen(row)">
              <td colspan="7" class="px-6 py-4 bg-gray-50">
                <div class="text-xs text-gray-600 mb-2 space-x-3 flex items-center justify-between">
                  <div class="space-x-3">
                    <span class="mr-3">Latest Sync Log</span>
                    <span v-if="logPanels[row.campaign.id]?.log">Status: <strong>{{ logPanels[row.campaign.id].log.status }}</strong></span>
                    <span v-if="logPanels[row.campaign.id]?.log?.action">Action: {{ logPanels[row.campaign.id].log.action }}</span>
                    <span v-if="logPanels[row.campaign.id]?.latestLogId">ID: {{ logPanels[row.campaign.id].latestLogId }}</span>
                    <span v-if="logPanels[row.campaign.id]?.log?.completed_at">Completed: {{ logPanels[row.campaign.id].log.completed_at }}</span>
                  </div>
                  <button type="button" class="text-xs text-blue-600 hover:text-blue-800"
                          @click="refreshOutput(row)">Refresh Output</button>
                </div>
                <div v-if="logPanels[row.campaign.id]?.log?.error_message" class="mb-2 text-xs text-red-600 flex items-center justify-between">
                  <div class="truncate">
                    <span class="font-semibold mr-1">Error:</span>
                    <span class="align-middle">{{ logPanels[row.campaign.id].log.error_message }}</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <button type="button" class="text-xs text-red-600 hover:text-red-800 flex-shrink-0"
                            @click="copyLogError(row)">Copy Error</button>
                    <button v-if="isDuplicateError(row)" type="button" class="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0"
                            @click="loadLeadCandidates(row)">Resolve Duplicate</button>
                  </div>
                </div>
                <div v-if="logPanels[row.campaign.id]?.loading" class="text-sm text-gray-500">Fetching log…</div>
                <div v-else>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div class="flex items-center justify-between mb-1">
                        <div class="text-xs font-semibold text-gray-700">Request Data</div>
                        <button type="button" class="text-xs text-blue-600 hover:text-blue-800"
                                @click="copyLogData(row, 'request_data')">Copy</button>
                      </div>
                      <pre class="text-xs bg-white border rounded p-3 overflow-auto" style="max-height: 280px;">{{ pretty(logPanels[row.campaign.id]?.log?.request_data) }}</pre>
                    </div>
                    <div>
                      <div class="flex items-center justify-between mb-1">
                        <div class="text-xs font-semibold text-gray-700">Response / Details</div>
                        <button type="button" class="text-xs text-blue-600 hover:text-blue-800"
                                @click="copyLogData(row, 'response_data')">Copy</button>
                      </div>
                      <pre class="text-xs bg-white border rounded p-3 overflow-auto" style="max-height: 280px;">{{ pretty(logPanels[row.campaign.id]?.log?.response_data) }}</pre>
                      <div v-if="!logPanels[row.campaign.id]?.log?.request_data && !logPanels[row.campaign.id]?.log?.response_data" class="text-xs text-gray-500 mt-2">
                        No request/response payload recorded for this log. See error above if present.
                      </div>
                    </div>
                  </div>
                  <!-- Candidate lead picker -->
                  <div v-if="candidatePanels[row.campaign.id]" class="mt-4 border-t pt-3">
                    <div class="text-sm font-medium text-gray-700 mb-2">Lead Candidates (company: {{ candidatePanels[row.campaign.id].company }}, state: {{ candidatePanels[row.campaign.id].state }})</div>
                    <!-- Quick search overrides -->
                    <form @submit.prevent="searchCandidates(row)" class="mb-2 flex flex-wrap items-center gap-2 text-xs">
                      <input v-model="candidatePanels[row.campaign.id].companyInput" type="text" placeholder="Company (override)" class="px-2 py-1 border rounded" />
                      <input v-model="candidatePanels[row.campaign.id].stateInput" type="text" placeholder="State" class="px-2 py-1 border rounded w-20" />
                      <input v-model="candidatePanels[row.campaign.id].cityInput" type="text" placeholder="City" class="px-2 py-1 border rounded w-28" />
                      <button type="submit" class="px-2 py-1 border rounded text-blue-600 hover:text-blue-800">Search</button>
                    </form>
                    <div v-if="candidatePanels[row.campaign.id].loading" class="text-xs text-gray-500">Loading candidates…</div>
                    <div v-else-if="(candidatePanels[row.campaign.id].list || []).length === 0" class="text-xs text-gray-500">No candidates found.</div>
                    <table v-else class="min-w-full text-xs border">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-2 py-1 text-left">Lead Id</th>
                          <th class="px-2 py-1 text-left">Name</th>
                          <th class="px-2 py-1 text-left">Email</th>
                          <th class="px-2 py-1 text-left">Company</th>
                          <th class="px-2 py-1 text-left">City/State</th>
                          <th class="px-2 py-1 text-left">Record Type</th>
                          <th class="px-2 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="lead in candidatePanels[row.campaign.id].list" :key="lead.Id" class="border-t">
                          <td class="px-2 py-1">{{ lead.Id }}</td>
                          <td class="px-2 py-1">{{ (lead.FirstName || '') + ' ' + (lead.LastName || '') }}</td>
                          <td class="px-2 py-1">{{ lead.Email || '—' }}</td>
                          <td class="px-2 py-1">{{ lead.Company }}</td>
                          <td class="px-2 py-1">{{ (lead.City || '—') + ', ' + (lead.State || '—') }}</td>
                          <td class="px-2 py-1">
                            <span v-if="lead.RecordTypeDetails && lead.RecordTypeDetails.Name" class="text-xs">
                              {{ lead.RecordTypeDetails.Name }}
                            </span>
                            <span v-else-if="lead.RecordType && lead.RecordType.Name" class="text-xs">
                              {{ lead.RecordType.Name }}
                            </span>
                            <span v-else class="text-gray-400">—</span>
                          </td>
                          <td class="px-2 py-1 text-right">
                            <button @click="applyCandidateLead(row, lead.Id)" class="px-2 py-1 border rounded text-blue-600 hover:text-blue-800">Use this Lead</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
            </tr>
            </template>
            <tr v-if="!loading && visibleRows.length === 0">
              <td colspan="7" class="px-6 py-8 text-center text-sm text-gray-500">No rows.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalesforceCampaignHealth',
  data() {
    return {
      loading: true,
      rows: [],
      campaigns: [],
      filters: {
        source: 'recent',
        days: 30,
        limit: 50,
        dp_name: '2025 - Dig Pink Rally',
      },
      showProblemsOnly: false,
      resyncing: {},
      refreshing: {},
      ignoring: {},
      progress: 0,
      progressTimer: null,
      chunkSize: 10,
      bypassCache: false,
      bulkResyncing: false,
      bulkResynced: 0,
      logPanels: {},
      candidatePanels: {},
      ignoredCampaigns: [],
      showIgnoredSection: false,
      loadingIgnored: false,
      nestedCampaigns: [],
      showNestedSection: false,
      loadingNested: false,
    }
  },
  watch: {
    filters: {
      handler() { this.savePrefs() },
      deep: true
    },
    chunkSize() { this.savePrefs() },
    showProblemsOnly() { this.savePrefs() },
    bypassCache() { this.savePrefs() }
  },
  computed: {
    visibleRows() {
      if (!this.showProblemsOnly) return this.rows
      return this.rows.filter(r => this.isProblemRow(r))
    },
    problemRowsCount() {
      return this.rows.filter(r => this.isProblemRow(r)).length
    }
  },
  methods: {
    loadPrefs() {
      try {
        const raw = localStorage.getItem('sfHealthPrefs')
        if (!raw) return
        const obj = JSON.parse(raw)
        if (obj && typeof obj === 'object') {
          if (obj.filters && typeof obj.filters === 'object') {
            this.filters = {
              source: obj.filters.source || this.filters.source,
              days: typeof obj.filters.days === 'number' ? obj.filters.days : this.filters.days,
              limit: typeof obj.filters.limit === 'number' ? obj.filters.limit : this.filters.limit,
              dp_name: obj.filters.dp_name ?? this.filters.dp_name,
            }
          }
          if ([5,10,20].includes(obj.chunkSize)) this.chunkSize = obj.chunkSize
          if (typeof obj.showProblemsOnly === 'boolean') this.showProblemsOnly = obj.showProblemsOnly
          if (typeof obj.bypassCache === 'boolean') this.bypassCache = obj.bypassCache
        }
      } catch (e) { /* ignore */ }
    },
    savePrefs() {
      try {
        const obj = {
          filters: this.filters,
          chunkSize: this.chunkSize,
          showProblemsOnly: this.showProblemsOnly,
          bypassCache: this.bypassCache,
        }
        localStorage.setItem('sfHealthPrefs', JSON.stringify(obj))
      } catch (e) { /* ignore */ }
    },
    async loadRows() {
      this.loading = true
      this.progress = 0
      this.rows = []
      this.campaigns = []
      try {
        // Step 1: get list only
        const listParams = new URLSearchParams()
        listParams.set('source', this.filters.source)
        listParams.set('days', String(this.filters.days))
        listParams.set('limit', String(this.filters.limit))
        listParams.set('list_only', '1')
        if (this.bypassCache) listParams.set('bypass_cache', '1')
        const listRes = await fetch('/admin/salesforce/health-report/data?' + listParams.toString())
        const listJson = await listRes.json()
        if (!listJson.success) throw new Error(listJson.message || 'Failed to load list')
        this.campaigns = listJson.campaigns || []
        if (this.campaigns.length === 0) {
          this.progress = 100
          return
        }

        // Step 2: fetch in chunks by ids
        const ids = this.campaigns.map(c => c.id)
        const chunks = []
        for (let i = 0; i < ids.length; i += this.chunkSize) {
          chunks.push(ids.slice(i, i + this.chunkSize))
        }

        let completed = 0
        for (const chunk of chunks) {
          const params = new URLSearchParams()
          params.set('source', this.filters.source)
          params.set('ids', chunk.join(','))
          if (this.filters.dp_name) params.set('dp_name', this.filters.dp_name)
          if (this.bypassCache) params.set('bypass_cache', '1')
          const res = await fetch('/admin/salesforce/health-report/data?' + params.toString())
          // If gateway times out, skip gracefully
          if (!res.ok) {
            console.warn('Chunk request failed', res.status)
            completed += chunk.length
            this.progress = Math.round((completed / ids.length) * 100)
            continue
          }
          const json = await res.json().catch(() => ({}))
          if (json && json.success && Array.isArray(json.rows)) {
            this.rows = this.rows.concat(json.rows)
          }
          completed += chunk.length
          this.progress = Math.round((completed / ids.length) * 100)
        }
      } catch (e) {
        console.error('Failed to load health data', e)
        alert('Failed to load Salesforce campaign health: ' + (e.message || e))
      } finally {
        this.loading = false
        setTimeout(() => { this.progress = 0 }, 800)
      }
    },
    startProgress() {
      // Reset and start an indeterminate progress that approaches 90%
      this.progress = 0
      if (this.progressTimer) clearInterval(this.progressTimer)
      this.progressTimer = setInterval(() => {
        if (this.progress < 90) {
          // Ease towards 90
          this.progress += Math.max(1, Math.round((90 - this.progress) * 0.05))
        }
      }, 200)
    },
    finishProgress() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
      this.progress = 100
      setTimeout(() => { this.progress = 0 }, 600)
    },
    badge(isGood) {
      return isGood
        ? 'px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'
        : 'px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'
    },
    rowClass(row) {
      return this.isProblemRow(row) ? 'bg-red-50' : ''
    },
    isProblemRow(row) {
      const problems = [
        row.metrics.unconverted_leads > 0,
        row.metrics.dig_pink_opps_count === 0,
        row.metrics.dig_pink_opps_count > 1,
        row.metrics.accounts_count > 1,
      ]
      return problems.some(Boolean)
    },
    async resync(row) {
      try {
        // Vue2 compat: set reactive prop
        this.$set ? this.$set(this.resyncing, row.campaign.id, true) : (this.resyncing = { ...this.resyncing, [row.campaign.id]: true })
        const tokenEl = document.querySelector('meta[name="csrf-token"]')
        const token = tokenEl ? tokenEl.getAttribute('content') : ''
        const res = await fetch(`/admin/salesforce/sync/${row.campaign.id}`, {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json'
          }
        })
        const json = await res.json().catch(() => ({}))
        if (res.ok && json.success) {
          this.$notify && this.$notify({ type: 'success', text: `Resync queued for campaign #${row.campaign.id}` })
          // Automatically open log panel and begin polling
          if (!this.isLogOpen(row)) {
            this.toggleLogPanel(row)
          } else {
            // ensure polling restarts
            const id = row.campaign.id
            this.stopPolling(id)
            this.ensurePolling(id)
          }
        } else {
          const msg = json.message || `Resync failed (HTTP ${res.status})`
          this.$notify && this.$notify({ type: 'error', text: msg })
        }
      } catch (e) {
        console.error('Resync error', e)
        this.$notify && this.$notify({ type: 'error', text: 'Resync request failed' })
      } finally {
        this.$set ? this.$set(this.resyncing, row.campaign.id, false) : (this.resyncing = { ...this.resyncing, [row.campaign.id]: false })
      }
    }
    ,
    async resyncAllProblems() {
      if (this.bulkResyncing) return
      const targets = this.rows.filter(r => this.isProblemRow(r))
      if (targets.length === 0) {
        this.$notify && this.$notify({ type: 'info', text: 'No problematic rows to resync' })
        return
      }
      this.bulkResyncing = true
      this.bulkResynced = 0
      for (const row of targets) {
        try {
          await this.resync(row)
        } catch (e) {
          // already handled inside resync
        }
        this.bulkResynced++
      }
      this.bulkResyncing = false
      this.$notify && this.$notify({ type: 'success', text: `Queued resync for ${this.bulkResynced} campaign(s)` })
    },
    async refreshRow(row) {
      try {
        // Vue2 compat: set reactive prop
        this.$set ? this.$set(this.refreshing, row.campaign.id, true) : (this.refreshing = { ...this.refreshing, [row.campaign.id]: true })
        const wasProblem = this.isProblemRow(row)
        const params = new URLSearchParams()
        params.set('ids', String(row.campaign.id))
        if (this.filters.dp_name) params.set('dp_name', this.filters.dp_name)
        params.set('bypass_cache', '1')
        const res = await fetch('/admin/salesforce/health-report/data?' + params.toString())
        if (!res.ok) {
          this.$notify && this.$notify({ type: 'error', text: `Refresh failed (HTTP ${res.status})` })
          return
        }
        const json = await res.json().catch(() => ({}))
        if (json && json.success && Array.isArray(json.rows) && json.rows.length > 0) {
          const updated = json.rows[0]
          const idx = this.rows.findIndex(r => r.campaign.id === row.campaign.id)
          if (idx !== -1) {
            this.$set ? this.$set(this.rows, idx, updated) : (this.rows = [
              ...this.rows.slice(0, idx),
              updated,
              ...this.rows.slice(idx + 1)
            ])
          }
          const nowProblem = this.isProblemRow(updated)
          this.$notify && this.$notify({ type: 'success', text: `Refreshed campaign #${row.campaign.id}` })
          if (this.showProblemsOnly) {
            if (wasProblem && !nowProblem) {
              this.$notify && this.$notify({ type: 'info', text: `Campaign #${row.campaign.id} resolved and removed by Problems-only filter` })
            } else if (!wasProblem && nowProblem) {
              this.$notify && this.$notify({ type: 'warn', text: `Campaign #${row.campaign.id} now flagged as a problem` })
            }
          }
        } else {
          this.$notify && this.$notify({ type: 'error', text: 'Refresh failed to return data' })
        }
      } catch (e) {
        console.error('Refresh row error', e)
        this.$notify && this.$notify({ type: 'error', text: 'Refresh request failed' })
      } finally {
        this.$set ? this.$set(this.refreshing, row.campaign.id, false) : (this.refreshing = { ...this.refreshing, [row.campaign.id]: false })
      }
    },
    toggleLogPanel(row) {
      const id = row.campaign.id
      const state = this.logPanels[id] || { open: false, loading: false, latestLogId: null, log: null, pollTimer: null }
      state.open = !state.open
      this.$set ? this.$set(this.logPanels, id, state) : (this.logPanels = { ...this.logPanels, [id]: state })
      if (state.open) {
        this.ensurePolling(id)
      } else {
        this.stopPolling(id)
      }
    },
    isLogOpen(row) {
      const id = row.campaign.id
      return !!(this.logPanels[id] && this.logPanels[id].open)
    },
    ensurePolling(campaignId) {
      const state = this.logPanels[campaignId]
      if (!state) return
      if (state.pollTimer) return
      state.loading = true
      const poll = async () => {
        try {
          const params = new URLSearchParams({ campaign_id: String(campaignId), per_page: '1', _: String(Date.now()) })
          const res = await fetch('/admin/salesforce/monitoring/logs?' + params.toString())
          if (!res.ok) return
          const json = await res.json().catch(() => ({}))
          const latest = json && json.data && json.data[0]
          if (latest && latest.id) {
            if (state.latestLogId !== latest.id) {
              state.latestLogId = latest.id
              await this.fetchLogDetail(campaignId, latest.id)
            } else if (state.log && state.log.status === 'pending') {
              await this.fetchLogDetail(campaignId, latest.id)
            }
          }
        } catch (_) {}
      }
      poll()
      state.pollTimer = setInterval(poll, 2000)
    },
    stopPolling(campaignId) {
      const state = this.logPanels[campaignId]
      if (!state) return
      if (state.pollTimer) {
        clearInterval(state.pollTimer)
        state.pollTimer = null
      }
    },
    async fetchLogDetail(campaignId, logId) {
      const state = this.logPanels[campaignId]
      if (!state) return
      try {
        state.loading = true
        const res = await fetch('/admin/salesforce/monitoring/logs/' + logId + '?_=' + Date.now())
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        state.log = json
      } catch (_) {
      } finally {
        state.loading = false
        if (state.log && state.log.status !== 'pending') {
          this.stopPolling(campaignId)
        }
      }
    },
    refreshOutput(row) {
      const id = row?.campaign?.id
      if (!id) return
      const state = this.logPanels[id] || { open: true }
      state.latestLogId = null
      state.log = null
      this.$set ? this.$set(this.logPanels, id, state) : (this.logPanels = { ...this.logPanels, [id]: state })
      this.stopPolling(id)
      this.ensurePolling(id)
    },
    pretty(obj) {
      try { return JSON.stringify(obj, null, 2) } catch { return String(obj) }
    },
    async copyText(text, successMsg = 'Copied') {
      try {
        if (!text) return
        await navigator.clipboard.writeText(text)
        this.$notify && this.$notify({ type: 'success', text: successMsg })
      } catch (e) {
        try {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.top = '-1000px'
          document.body.appendChild(ta)
          ta.focus()
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
          this.$notify && this.$notify({ type: 'success', text: successMsg })
        } catch (_) {
          this.$notify && this.$notify({ type: 'error', text: 'Copy failed' })
        }
      }
    },
    copyRowEmail(row) {
      if (!row || !row.email) return
      this.copyText(row.email, `Email copied: ${row.email}`)
    },
    copyLogData(row, key) {
      try {
        const cid = row?.campaign?.id
        if (!cid) return
        const data = this.logPanels[cid]?.log?.[key]
        if (data === undefined) {
          this.$notify && this.$notify({ type: 'error', text: 'No data to copy yet' })
          return
        }
        const text = typeof data === 'string' ? data : this.pretty(data)
        const label = key === 'request_data' ? 'Request data' : 'Response data'
        this.copyText(text, `${label} copied`)
      } catch (e) {
        this.$notify && this.$notify({ type: 'error', text: 'Copy failed' })
      }
    },
    copyLogError(row) {
      try {
        const cid = row?.campaign?.id
        if (!cid) return
        const err = this.logPanels[cid]?.log?.error_message
        if (!err) {
          this.$notify && this.$notify({ type: 'error', text: 'No error to copy' })
          return
        }
        this.copyText(err, 'Error copied')
      } catch (_) {
        this.$notify && this.$notify({ type: 'error', text: 'Copy failed' })
      }
    },
    isDuplicateError(row) {
      const err = this.logPanels[row.campaign.id]?.log?.error_message || ''
      const s = err.toLowerCase()
      return s.includes('duplicates_detected') || s.includes('cannot_execute_flow_trigger')
    },
    async loadLeadCandidates(row) {
      const id = row.campaign.id
      const state = this.candidatePanels[id] || { loading: true, list: [] }
      state.loading = true
      this.$set ? this.$set(this.candidatePanels, id, state) : (this.candidatePanels = { ...this.candidatePanels, [id]: state })
      try {
        const res = await fetch(`/admin/salesforce/duplicates/candidates?campaign_id=${id}&_=${Date.now()}`)
        const json = await res.json()
        state.company = json.company
        state.state = json.state
        state.city = json.city
        state.list = json.candidates || []
      } catch (e) {
        this.$notify && this.$notify({ type: 'error', text: 'Failed to load lead candidates' })
        state.list = []
      } finally {
        state.loading = false
        this.$set ? this.$set(this.candidatePanels, id, state) : (this.candidatePanels = { ...this.candidatePanels, [id]: state })
      }
    },
    async searchCandidates(row) {
      const id = row.campaign.id
      const panel = this.candidatePanels[id]
      if (!panel) return
      const params = new URLSearchParams({ campaign_id: String(id), _: String(Date.now()) })
      if (panel.companyInput && panel.companyInput.trim()) params.set('company', panel.companyInput.trim())
      if (panel.stateInput && panel.stateInput.trim()) params.set('state', panel.stateInput.trim())
      if (panel.cityInput && panel.cityInput.trim()) params.set('city', panel.cityInput.trim())
      panel.loading = true
      this.$set ? this.$set(this.candidatePanels, id, panel) : (this.candidatePanels = { ...this.candidatePanels, [id]: panel })
      try {
        const res = await fetch('/admin/salesforce/duplicates/candidates?' + params.toString())
        const json = await res.json()
        panel.company = json.company
        panel.state = json.state
        panel.city = json.city
        panel.list = json.candidates || []
      } catch (e) {
        this.$notify && this.$notify({ type: 'error', text: 'Search failed' })
      } finally {
        panel.loading = false
        this.$set ? this.$set(this.candidatePanels, id, panel) : (this.candidatePanels = { ...this.candidatePanels, [id]: panel })
      }
    },
    async applyCandidateLead(row, leadId) {
      const id = row.campaign.id
      try {
        const tokenEl = document.querySelector('meta[name="csrf-token"]')
        const token = tokenEl ? tokenEl.getAttribute('content') : ''
        const res = await fetch('/admin/salesforce/duplicates/update-lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token, 'Accept': 'application/json' },
          body: JSON.stringify({ campaign_id: id, lead_id: leadId })
        })
        const json = await res.json().catch(() => ({}))
        if (res.ok && json.success) {
          this.$notify && this.$notify({ type: 'success', text: 'Lead updated; conversion will run shortly' })
          // Open output and refresh polling
          if (!this.isLogOpen(row)) this.toggleLogPanel(row)
          else { const cid = id; this.stopPolling(cid); this.ensurePolling(cid) }
        } else {
          const msg = json.message || `Lead update failed (HTTP ${res.status})`
          this.$notify && this.$notify({ type: 'error', text: msg })
        }
      } catch (e) {
        this.$notify && this.$notify({ type: 'error', text: 'Request failed' })
      }
    },
    async ignoreCampaign(row) {
      const reason = prompt('Optional: Enter a reason for ignoring this campaign (or leave blank):')
      if (reason === null) return // User cancelled
      
      try {
        this.$set ? this.$set(this.ignoring, row.campaign.id, true) : (this.ignoring = { ...this.ignoring, [row.campaign.id]: true })
        const tokenEl = document.querySelector('meta[name="csrf-token"]')
        const token = tokenEl ? tokenEl.getAttribute('content') : ''
        const res = await fetch(`/admin/salesforce/health-report/ignore/${row.campaign.id}`, {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason })
        })
        const json = await res.json().catch(() => ({}))
        if (res.ok && json.success) {
          this.$notify && this.$notify({ type: 'success', text: `Campaign #${row.campaign.id} ignored for 30 days` })
          // Remove from current rows
          const idx = this.rows.findIndex(r => r.campaign.id === row.campaign.id)
          if (idx !== -1) {
            this.rows.splice(idx, 1)
          }
          // Refresh ignored list
          this.loadIgnoredCampaigns()
        } else {
          const msg = json.message || `Ignore failed (HTTP ${res.status})`
          this.$notify && this.$notify({ type: 'error', text: msg })
        }
      } catch (e) {
        console.error('Ignore error', e)
        this.$notify && this.$notify({ type: 'error', text: 'Ignore request failed' })
      } finally {
        this.$set ? this.$set(this.ignoring, row.campaign.id, false) : (this.ignoring = { ...this.ignoring, [row.campaign.id]: false })
      }
    },
    async unignoreCampaign(campaignId) {
      if (!confirm('Are you sure you want to remove the ignore status for this campaign?')) return
      
      try {
        const tokenEl = document.querySelector('meta[name="csrf-token"]')
        const token = tokenEl ? tokenEl.getAttribute('content') : ''
        const res = await fetch(`/admin/salesforce/health-report/ignore/${campaignId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-TOKEN': token,
            'Accept': 'application/json'
          }
        })
        const json = await res.json().catch(() => ({}))
        if (res.ok && json.success) {
          this.$notify && this.$notify({ type: 'success', text: `Campaign #${campaignId} unignored` })
          // Refresh ignored list
          this.loadIgnoredCampaigns()
          // Optionally refresh main list
          if (this.showProblemsOnly || this.filters.source === 'recent') {
            this.loadRows()
          }
        } else {
          const msg = json.message || `Unignore failed (HTTP ${res.status})`
          this.$notify && this.$notify({ type: 'error', text: msg })
        }
      } catch (e) {
        console.error('Unignore error', e)
        this.$notify && this.$notify({ type: 'error', text: 'Unignore request failed' })
      }
    },
    async loadIgnoredCampaigns() {
      this.loadingIgnored = true
      try {
        const res = await fetch('/admin/salesforce/health-report/ignored')
        const json = await res.json().catch(() => ({}))
        if (json && json.success) {
          this.ignoredCampaigns = json.data || []
        }
      } catch (e) {
        console.error('Failed to load ignored campaigns', e)
      } finally {
        this.loadingIgnored = false
      }
    },
    toggleIgnoredSection() {
      this.showIgnoredSection = !this.showIgnoredSection
      if (this.showIgnoredSection && this.ignoredCampaigns.length === 0) {
        this.loadIgnoredCampaigns()
      }
    },
    toggleNestedSection() {
      this.showNestedSection = !this.showNestedSection
      if (this.showNestedSection && this.nestedCampaigns.length === 0) {
        this.loadNestedCampaigns()
      }
    },
    async loadNestedCampaigns() {
      this.loadingNested = true
      try {
        const res = await fetch('/admin/salesforce/health-report/nested-campaigns?_=' + Date.now())
        const json = await res.json()
        if (json && json.success) {
          this.nestedCampaigns = json.data || []
        }
      } catch (e) {
        console.error('Failed to load nested campaigns', e)
        this.$notify && this.$notify({ type: 'error', text: 'Failed to load nested campaigns' })
      } finally {
        this.loadingNested = false
      }
    }
  },
  mounted() {
    this.loadPrefs()
    this.loadRows()
    this.loadIgnoredCampaigns() // Load ignored campaigns to get the count
  }
}
</script>

<style scoped>
</style>
